import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import uniqBy from 'lodash/uniqBy';

import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {ListResponseType} from 'sweet-types';

export type InfiniteListFetcherHookProps<
  IT extends {id: number | string},
  FP extends {page: string}
> = {
  page?: number;
  pageParam?: string;
  fetchParams: Record<string, any>;
  useDataHook: UseListQueryType<FP, IT>;
  paramsConverter: (query: Record<string, any>) => FP;
};

type PageResult<IT extends {id: number | string}> = {
  data?: ListResponseType<IT, any>;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
};

// The caller MUST render the returned `subscriptions` node, e.g.
// const {results, subscriptions} = useInfiniteListFetcherRTK(...);
// return <>{subscriptions}{rest of UI}</>;
// If you don't need to control where that node goes, prefer the
// `InfiniteListRTK` wrapper component, which renders it for you.

export function useInfiniteListFetcherRTK<
  IT extends {id: number | string},
  FP extends {page: string}
>({
  page = 1,
  pageParam,
  fetchParams,
  useDataHook,
  paramsConverter,
}: InfiniteListFetcherHookProps<IT, FP>) {
  const [params, setParams] = useState<Record<string, any>>(fetchParams);
  // Page numbers that currently have a live subscription, in load order.
  const [pages, setPages] = useState<Array<number>>([page]);
  // Latest reported result for every mounted page subscription.
  const [pageResults, setPageResults] = useState<
    Record<number, PageResult<IT>>
  >({});

  // Each page registers its `refetch` so `refetch()` can refresh every page.
  const refetchers = useRef<Record<number, (() => unknown) | undefined>>({});

  // When the filters change, drop every loaded page and start over at page one.
  // We deliberately do NOT clear `pageResults`/`refetchers` here. Effects run
  // child-before-parent, so this (parent) effect runs after each PageSubscription
  // has already reported, and clearing would wipe a just-reported result that the
  // child won't re-send when its data is unchanged (e.g. served from cache).
  useEffect(() => {
    setParams(fetchParams);
    setPages([page]);
  }, [JSON.stringify(fetchParams)]);

  const handleResult = useCallback(
    (reportedPage: number, result: PageResult<IT> | undefined) => {
      setPageResults((prevResults) => {
        // Results are empty
        if (result === undefined) {
          // We didn't even had the page that we got results for
          if (!(reportedPage in prevResults)) {
            return prevResults;
          }

          // We can get rid of this page - since it returned no results
          const nextResults = {...prevResults};
          delete nextResults[reportedPage];
          return nextResults;
        }
        return {...prevResults, [reportedPage]: result};
      });
    },
    []
  );

  const registerRefetch = useCallback(
    (reportedPage: number, refetch: (() => unknown) | undefined) => {
      if (refetch === undefined) {
        delete refetchers.current[reportedPage];
      } else {
        refetchers.current[reportedPage] = refetch;
      }
    },
    []
  );

  // One live RTK Query subscription per loaded page.
  const subscriptions = (
    <>
      {pages.map((pageNumber) => (
        <PageSubscription<IT, FP>
          key={pageNumber}
          page={pageNumber}
          params={params}
          pageParam={pageParam}
          useDataHook={useDataHook}
          paramsConverter={paramsConverter}
          onResult={handleResult}
          registerRefetch={registerRefetch}
        />
      ))}
    </>
  );

  const results: Array<IT> = useMemo(
    () =>
      uniqBy(
        pages.flatMap(
          (pageNumber) => pageResults[pageNumber]?.data?.results ?? []
        ),
        (item) => item.id
      ),
    [pages, pageResults]
  );

  const firstResult = pageResults[pages[0]];
  const lastResult = pageResults[pages[pages.length - 1]];

  const loadNext = useCallback(() => {
    setPages((prev) =>
      prev.length === 0 ? [page] : [...prev, prev[prev.length - 1] + 1]
    );
  }, [page]);

  const refetch = useCallback(
    () =>
      Promise.all(
        Object.values(refetchers.current)
          .filter((fn): fn is () => unknown => fn != null)
          .map((fn) => fn())
      ),
    []
  );

  return {
    results,
    loadNext,
    hasNext: lastResult?.data?.next !== null,
    isLoading: firstResult?.isLoading ?? true,
    isFetching: Object.values(pageResults).some((result) => result.isFetching),
    error: lastResult?.error,
    totalCount: firstResult?.data?.count ?? 0,
    refetch,
    subscriptions,
  };
}

type PageSubscriptionProps<
  IT extends {id: number | string},
  FP extends {page: string}
> = {
  page: number;
  params: Record<string, any>;
  pageParam?: string;
  useDataHook: UseListQueryType<FP, IT>;
  paramsConverter: (query: Record<string, any>) => FP;
  onResult: (page: number, result: PageResult<IT> | undefined) => void;
  registerRefetch: (page: number, refetch: (() => unknown) | undefined) => void;
};

function PageSubscription<
  IT extends {id: number | string},
  FP extends {page: string}
>({
  page,
  params,
  pageParam,
  useDataHook,
  paramsConverter,
  onResult,
  registerRefetch,
}: PageSubscriptionProps<IT, FP>) {
  const {data, isLoading, isFetching, error, refetch} = useDataHook(
    paramsConverter({
      ...params,
      [pageParam || 'page']: page.toString(),
    }),
    {refetchOnMountOrArgChange: 60}
  );

  // Report this page's latest state upward whenever it changes.
  useEffect(() => {
    onResult(page, {data, isLoading, isFetching, error});
  }, [page, data, isLoading, isFetching, error, onResult]);

  // Keep this page's refetch reachable so the list can refresh every page.
  useEffect(() => {
    registerRefetch(page, refetch);
    return () => registerRefetch(page, undefined);
  }, [page, refetch, registerRefetch]);

  // Drop this page's contribution only once it is actually unmounted.
  useEffect(() => () => onResult(page, undefined), [page, onResult]);

  return null;
}
